import { Text, VStack } from '@chakra-ui/react';
import { useCallback, useState } from 'react';
import { SPREADSHEET_MAX_BYTES } from '@bower/shared/constants/spreadsheet-intake';

import type { useCreateSpreadsheet } from '../../hooks/entity/useCreateSpreadsheet';
import { EntryOption } from '../features/create/EntryOption';
import { OrDivider } from '../features/create/OrDivider';
import { BowerIcon } from '../ui/BowerIcon';
import { DropZone } from '../upload/intake/DropZone';
import { SPREADSHEET } from '../upload/intake/rules';
import { useFileIntake } from '../upload/intake/useFileIntake';

export interface CreateSpreadsheetPanelProps {
  /** The collection to create in; omitted, the workspace root. */
  readonly projectId?: string;
  /** The spreadsheet exists and is opening: the container closes. */
  readonly onDone: () => void;
  /**
   * The create, owned by the container (`useCreateSpreadsheet`) so it reads
   * `isBusy` in the same render and holds its close controls without a lag.
   */
  readonly spreadsheet: ReturnType<typeof useCreateSpreadsheet>;
}

/**
 * "New spreadsheet": drop or pick a file, or start with a blank sheet.
 *
 * No confirm step for a picked file, unlike the protocol panel: importing a
 * spreadsheet starts no paid model turn, and a wrong pick is undone by
 * deleting it.
 */
export function CreateSpreadsheetPanel({
  projectId,
  onDone,
  spreadsheet,
}: CreateSpreadsheetPanelProps) {
  const { importFile, createBlank, isBusy } = spreadsheet;
  // Which action is busy: an import only holds for its byte upload.
  const [isUploading, setIsUploading] = useState(false);
  const working = isBusy || isUploading;

  const handleFile = useCallback(
    async (file: File) => {
      setIsUploading(true);
      try {
        if (await importFile(file, { projectId })) onDone();
      } finally {
        setIsUploading(false);
      }
    },
    [projectId, importFile, onDone],
  );

  const intake = useFileIntake({
    rule: SPREADSHEET,
    surface: 'create_spreadsheet',
    maxFiles: 1,
    maxBytes: SPREADSHEET_MAX_BYTES,
    // A second file mid-upload is refused out loud, never silently dropped.
    readOnlyReason: working ? 'Wait for this one to finish first.' : undefined,
    onFiles: ([file]) => void handleFile(file),
  });

  const handleBlank = useCallback(async () => {
    if (await createBlank({ projectId })) onDone();
  }, [projectId, createBlank, onDone]);

  return (
    <VStack align="stretch" spacing={3}>
      {/* Mounted from the start, only its text changes: a live region that
          appears together with its text is not announced. Empty, it is 0px. */}
      <Text
        fontSize="sm"
        color="text.secondary"
        aria-live="polite"
        data-testid="spreadsheet-busy-status"
        mb={working ? 0 : -3}
      >
        {working && (isUploading ? 'Uploading your file…' : 'Setting up your spreadsheet…')}
      </Text>
      <DropZone
        intake={intake}
        icon="table_view"
        title="Drop a CSV or XLSX, or click to choose"
        next="It opens as a live sheet Bower can read and chart."
      />
      <OrDivider />
      <EntryOption
        icon={<BowerIcon name="spreadsheet" size="20px" gradient="warm" />}
        title="Start with a blank sheet"
        description="Add columns and rows yourself as you go."
        onClick={() => void handleBlank()}
        isDisabled={working}
      />
    </VStack>
  );
}
